'use client'

import clsx from 'clsx'
import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import { useSearchParams } from 'next/navigation'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'

import {
  BookingWidgetData,
  BookingWidgetStickyContainer,
} from 'lib/components/BookingWidget/BookingWidget'
import HomepageHero from 'lib/components/HomepageHero/HomepageHero'

import { autoCompleteAddressToShipPoint, useClassifyAddressApi } from '@/api/useClassifyAddressApi'
import { useCutoffTimesApi } from '@/api/useCutoffTimesApi'
import {
  computePricesForCarrierServices,
  useCarrierServiceApi,
} from '@/app/book/api/useCarrierServiceApi'
import {
  productLinesToProductIds,
  productLinesToProducts,
  useProductLineApi,
} from '@/app/book/api/useProductLineApi'
import { useProductLinePricingApi } from '@/app/book/api/useProductLinePricingApi'
import useCartStore from '@/app/book/stores/useCartStore'
import useShipmentStore from '@/app/book/stores/useShipmentStore'
import { useAutoCompleteAddress } from '@/hooks/useAutoCompleteAddress'
import { useBookingWidgetTracking } from '@/hooks/useBookingWidgetTracking'
import { usePricingExperiments } from '@/hooks/usePricingExperiments'
import type { ShipRoute } from '@/types/order'
import { buildBookingWidgetQuery, redirectFromBookingWidget } from '@/utils/bookingWidgetRedirect'
import { Key as BrandName, brand } from '@/utils/brand'
import { convertToISO8601, today } from '@/utils/dateHelpers'

const BookingWidgetPage: React.FC = () => {
  const { heroMeta } = brand.pageContent.Home
  const {
    trackOriginEntered,
    trackDestinationEntered,
    trackTripTypeSelected,
    trackCheckoutStarted,
  } = useBookingWidgetTracking()
  const searchParams = useSearchParams()
  const [selectedProducts, setSelectedProducts] = useState<OrderProductLabel[]>([])
  const shippingMatrix = useMemo<Record<BrandName, number>>(
    () => ({
      shipcamps: 11900,
      shipplay: 4900,
      shipskis: 7000,
      shipsticks: 5400,
    }),
    [],
  )
  const [totalPrice, setTotalPrice] = useState<number>(shippingMatrix[brand.key])
  const [tripType, setTripType] = useState<'oneway' | 'roundtrip'>('roundtrip')
  const [stickyReady, setStickyReady] = useState(false)

  const initRoute = useShipmentStore((state) => state.initRoute)

  const setShipmentFromBookingWidget = useShipmentStore(
    (state) => state.setShipmentFromBookingWidget,
  )

  const clearCart = useCartStore((state) => state.clearCart)
  const setSelectedProductsFromBookingWidget = useCartStore((state) => state.setSelectedProducts)

  const [shipRoute, setShipRoute] = useState<ShipRoute>(initRoute())

  const {
    address: originAddress,
    autoCompleteItems: originAutoCompleteItems,
    isLoadingAutoComplete: originIsLoadingAutoComplete,
    onInputSearchTerm: originOnInputSearchTerm,
    onSelectAutoCompleteItem: originOnSelectAutoCompleteItem,
    recognizedPlace: originRecognizedPlace,
    searchTerm: originSearchTerm,
  } = useAutoCompleteAddress()

  const {
    address: destinationAddress,
    autoCompleteItems: destinationAutoCompleteItems,
    isLoadingAutoComplete: destinationIsLoadingAutoComplete,
    onInputSearchTerm: destinationOnInputSearchTerm,
    onSelectAutoCompleteItem: destinationOnSelectAutoCompleteItem,
    recognizedPlace: destinationRecognizedPlace,
    searchTerm: destinationSearchTerm,
  } = useAutoCompleteAddress()

  const trackAndSelectOrigin = useCallback(
    (item: AutoCompleteItem): void => {
      trackOriginEntered(item?.formatted_address)
      originOnSelectAutoCompleteItem(item)
    },
    [originOnSelectAutoCompleteItem, trackOriginEntered],
  )

  const trackAndSelectDestination = useCallback(
    (item: AutoCompleteItem): void => {
      trackDestinationEntered(item?.formatted_address)
      destinationOnSelectAutoCompleteItem(item)
    },
    [destinationOnSelectAutoCompleteItem, trackDestinationEntered],
  )

  const { mutateAsync: classifyAddress, isPending: classifyAddressIsLoading } =
    useClassifyAddressApi()

  // TODO: loading and errors
  const { data: productLines } = useProductLineApi(shipRoute)

  const defaultProductLineName = useMemo(() => (brand.key === 'shipskis' ? 'Snowboot' : 'Golf'), [])

  const pricingProductId = useMemo(
    () =>
      productLines?.find(({ name }) => name === defaultProductLineName)?.labels?.[0]?.id ||
      productLines?.[0]?.labels?.[0]?.id ||
      '',
    [productLines, defaultProductLineName],
  )

  const productIds = useMemo(() => productLinesToProductIds(productLines || []), [productLines])

  const { getVariationId } = usePricingExperiments()

  const variationId = useMemo(() => getVariationId(), [getVariationId])

  // TODO: loading and errors
  const { data: pricingData } = useProductLinePricingApi({
    ...shipRoute,
    productIds,
    experimentVariationId: variationId,
  })

  const products = useMemo(
    () => productLinesToProducts(productLines || [], pricingData || []),
    [productLines, pricingData],
  )

  const { data: cutoffTimes } = useCutoffTimesApi()

  const earliestOutboundDate = useMemo(
    () => (cutoffTimes?.pickupDate ? convertToISO8601(cutoffTimes?.pickupDate) : today()),
    [cutoffTimes],
  )

  // TODO: loading and errors
  const { data: carrierServices, isLoading: isLoadingServiceLevels } = useCarrierServiceApi(
    shipRoute,
    selectedProducts.map(({ id }) => id),
    earliestOutboundDate,
    true,
    'outbound',
  )

  const serviceOptions = useMemo(
    () => computePricesForCarrierServices(carrierServices || [], selectedProducts),
    [carrierServices, selectedProducts],
  )

  const handleDataChange = useCallback(
    (data?: BookingWidgetData): void => {
      if (data && 'tripType' in data && data?.tripType !== tripType) {
        trackTripTypeSelected(data?.tripType)

        setTripType((data?.tripType || 'roundtrip') as 'oneway' | 'roundtrip')
      }

      if (data?.products?.length) {
        const priceCents = data?.products?.reduce(
          (acc, product) => acc + (product.price_cents || 0),
          0,
        )
        setTotalPrice(priceCents)
      }

      if (data?.services?.length) {
        const priceCents = data?.services?.reduce(
          (acc, service) => acc + (service?.totalComputedPriceNote || 0),
          0,
        )
        setTotalPrice(priceCents)
      }

      if (data && data?.products?.length) setSelectedProducts(data?.products)
    },
    [setTotalPrice, trackTripTypeSelected, tripType],
  )

  const handleOnSubmit = (data?: BookingWidgetData): void => {
    if (data) {
      trackCheckoutStarted({
        tripType: data?.tripType === 'oneway' ? 'oneway' : 'roundtrip',
        origin: originAddress?.formatted_address,
        destination: destinationAddress?.formatted_address,
      })

      setShipmentFromBookingWidget(
        data,
        [originRecognizedPlace, destinationRecognizedPlace],
        [
          {
            address_1: originAddress?.address_1,
            address_2: originAddress?.address_2,
            company_name: originAddress?.company_name,
            country_code: originAddress?.country_code,
            formatted_address: originAddress?.formatted_address,
            address_type: shipRoute.origin.addressType || '',
            state: originAddress?.state,
            city: originAddress?.city,
            zip: originAddress?.zip,
          },
          {
            address_1: destinationAddress?.address_1,
            address_2: destinationAddress?.address_2,
            company_name: destinationAddress?.company_name,
            country_code: destinationAddress?.country_code,
            formatted_address: destinationAddress?.formatted_address,
            address_type: shipRoute.destination.addressType || '',
            state: destinationAddress?.state,
            city: destinationAddress?.city,
            zip: destinationAddress?.zip,
          },
        ],
        shipRoute,
      )
    }

    // Clear previous carts
    clearCart()

    if (data?.products) setSelectedProductsFromBookingWidget(data?.products)

    const queryString = buildBookingWidgetQuery({
      originAddress,
      destinationAddress,
      originRecognizedPlace,
      destinationRecognizedPlace,
      tripType: data?.tripType,
      searchParams,
    })

    redirectFromBookingWidget(queryString)
  }

  const formatTotalPrice = useCallback(() => {
    const price = Math.ceil(totalPrice / 100)
    const formattedPrice = price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    })
    return formattedPrice || '$0.00'
  }, [totalPrice])

  const addresses = useMemo(
    () => [[originAddress, destinationAddress]],
    [originAddress, destinationAddress],
  )
  /* Set the total price to the base price when the product changes */
  useEffect(() => {
    const price =
      pricingData?.find(({ productId }) => productId === pricingProductId)?.priceCents || 0

    if (price) setTotalPrice(price)
  }, [pricingData, pricingProductId])

  /* Reset the total price to the base price when the addresses are cleared */
  useEffect(() => {
    if (!destinationSearchTerm || !originSearchTerm) setTotalPrice(shippingMatrix[brand.key])
  }, [destinationSearchTerm, originSearchTerm, shippingMatrix])

  useEffect(() => {
    setShipRoute({
      origin: {
        address1: originAddress?.address_1,
        address2: originAddress?.address_2,
        countryCode: originAddress?.country_code,
        companyName: originAddress?.company_name,
        state: originAddress?.state,
        city: originAddress?.city,
        postalCode: originAddress?.zip,
      },
      destination: {
        address1: destinationAddress?.address_1,
        address2: destinationAddress?.address_2,
        countryCode: destinationAddress?.country_code,
        companyName: destinationAddress?.company_name,
        state: destinationAddress?.state,
        city: destinationAddress?.city,
        postalCode: destinationAddress?.zip,
      },
    })
  }, [originAddress, destinationAddress])

  useEffect(() => {
    async function classifyShipPoints(): Promise<void> {
      if (originAddress && destinationAddress) {
        const results = await classifyAddress({
          shipPoints: [
            autoCompleteAddressToShipPoint(originAddress),
            autoCompleteAddressToShipPoint(destinationAddress),
          ],
        })

        const classifications = results?.classifications

        const originAddressType = classifications?.[0]?.addressType
        const destinationAddressType = classifications?.[1]?.addressType

        if (originAddressType && !originAddress.address_type) {
          setShipRoute(({ origin, destination }) => ({
            origin: { ...origin, addressType: originAddressType },
            destination,
          }))
        }

        if (destinationAddressType && !destinationAddress.address_type) {
          setShipRoute(({ origin, destination }) => ({
            destination: { ...destination, addressType: destinationAddressType },
            origin,
          }))
        }
      }
    }

    classifyShipPoints()
  }, [originAddress, destinationAddress, classifyAddress])

  useEffect(() => {
    let cancelled = false
    const enableSticky = (): void => {
      if (!cancelled) setStickyReady(true)
    }

    const idleWindow = window as Window & {
      requestIdleCallback?: (cb: () => void) => number
      cancelIdleCallback?: (handle: number) => void
    }
    const idleHandle = idleWindow.requestIdleCallback?.(enableSticky)
    const timeoutId = window.setTimeout(enableSticky, 1500)

    return () => {
      cancelled = true
      if (idleHandle) idleWindow.cancelIdleCallback?.(idleHandle)
      window.clearTimeout(timeoutId)
    }
  }, [])

  const stickyContainerClassName = clsx(
    'fixed left-0 z-10 w-full',
    'top-[calc(var(--braze-banner-height,0px)+58px)]',
    'lg:top-[calc(var(--braze-banner-height,0px)+80px)]',
  )

  return (
    <>
      {stickyReady && (
        <div className={stickyContainerClassName}>
          <Suspense fallback={null}>
            <BookingWidgetStickyContainer />
          </Suspense>
        </div>
      )}
      <Suspense fallback={null}>
        <HomepageHero
          bookingWidget={{
            addresses,
            autoCompleteItems: [[originAutoCompleteItems, destinationAutoCompleteItems]],
            areAutoCompleteItemsLoading: [
              [originIsLoadingAutoComplete, destinationIsLoadingAutoComplete],
            ],
            brand: brand.key,
            loadingServiceLevels: isLoadingServiceLevels || classifyAddressIsLoading,
            noSticky: !stickyReady,
            totalPriceFormatted: formatTotalPrice(),
            onChange: handleDataChange,
            onSubmit: handleOnSubmit,
            onInputSearchTermHandlers: [[originOnInputSearchTerm, destinationOnInputSearchTerm]],
            onSelectAutoCompleteItemHandlers: [[trackAndSelectOrigin, trackAndSelectDestination]],
            productOptions: products,
            serviceOptions: serviceOptions,
          }}
          {...heroMeta}
        />
      </Suspense>
    </>
  )
}

export default BookingWidgetPage
